import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SecurityService } from 'src/app/services/security-service/security.service';
import { CompanyData } from 'src/app/models/company-data-model/company-data.model';
import { MenuSection } from 'src/app/models/menu-section-model/menu-section.model';
import { UserData } from 'src/app/models/user-data-model/user-data.model';
import { CenterLite } from 'src/app/models/center-model/center-lite.model';
import { SocketChannel } from 'src/app/models/communication-models/socket-channel.model';
import { LoginVerificationChallenge } from 'src/app/models/login-verification-model/login-verification-challenge.model';
import { LoginVerificationContact } from 'src/app/models/login-verification-model/login-verification-contact.model';

@Injectable()
export class UserStorageService {

  private MBASE_UDID_ID = 'mb_ui';
  private MBASE_LANG_ID = 'mb_la';
  private MBASE_CONTEXT_ID = 'mb_cx';
  private MBASE_COMPANYDATA_ID = 'mb_cd';
  private MBASE_USERDATA_ID = 'mb_ud';
  private MBASE_CENTERLIST_ID = 'mb_cl';
  private MBASE_SOCKETCHANNELS_ID = 'mb_sc';
  private MBASE_MENUSECTIONLIST_ID = 'mb_mc';
  private MBASE_BO_VERSION = 'mb_vv';
  private MBASE_OAUTH_TOKEN = 'mb_oa';
  private MBASE_FILTER_DEVICES = 'mb_fide';
  private MBASE_FILTER_CATALOGS = 'mb_ficata';
  private MBASE_FILTER_BROADCASTINGS = 'mb_fibroad';
  private MBASE_FILTER_CONTEXTS = 'mb_fictx';
  private MBASE_FILTER_CENTERS = 'mb_ficent';
  private MBASE_FILTER_NOTIFICATIONS = 'mb_finotif';
  private MBASE_FILTER_TICKETS = 'mb_fitick';
  private MBASE_FILTER_LOCATIONS = 'mb_filoca';
  private MBASE_FILTER_FORMS = 'mb_fiform';
  private MBASE_FILTER_ALERTS = 'mb_fialert';
  private MBASE_FILTER_STAFFS = 'mb_fistaff';
  private MBASE_FILTER_DEPARTMENTS = 'mb_fidepart';
  private MBASE_FILTER_DESKS = 'mb_fidesk';
  private MBASE_FILTER_GATEWAYS = 'mb_figate';
  private MBASE_FILTER_LICENSES = 'mb_filic';
  private MBASE_COMPANY_LIST = 'mb_c_list';
  private MBASE_TMP_CONTEXT = 'mb_tmp_cx';
  private MBASE_LOGIN_VERIFICATION_CHALLENGE = 'mb_lvc';

  constructor(private router: Router,
    private securityService: SecurityService) { }

  public setUDID(udid) {
    if (udid) {
      try {
        sessionStorage.setItem(this.MBASE_UDID_ID, this.securityService.encrypt(udid));
        return true;
      } catch (e) {
        console.log('Fail saving udid token in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }

  public setLang(lang) {
    if (lang) {
      try {
        sessionStorage.setItem(this.MBASE_LANG_ID, this.securityService.encrypt(lang));
        return true;
      } catch (e) {
        console.log('Fail saving lang token in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }

  public setContext(context) {
    if (context) {
      try {
        sessionStorage.setItem(this.MBASE_CONTEXT_ID, this.securityService.encrypt(context));
        return true;
      } catch (e) {
        console.log('Fail saving context token in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }

  public setCompanyList(companyList) {
    if (companyList) {
      try {
        sessionStorage.setItem(this.MBASE_COMPANY_LIST, this.securityService.encrypt(JSON.stringify(companyList)));
        return true;
      } catch (e) {
        console.log('Fail saving company list data in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }

  public setTmpContext(tmpContext) {
    if (tmpContext) {
      try {
        sessionStorage.setItem(this.MBASE_TMP_CONTEXT, this.securityService.encrypt(tmpContext));
        return true;
      } catch (e) {
        console.log('Fail saving temporal context token in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }

  public setLoginVerificationChallenge(loginVerificationChallenge) {
    if (loginVerificationChallenge) {
      try {
        sessionStorage.setItem(this.MBASE_LOGIN_VERIFICATION_CHALLENGE, this.securityService.encrypt(JSON.stringify(loginVerificationChallenge)));
        return true;
      } catch (e) {
        console.log('Fail saving login verification challenge in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }

  public setCompanyData(companyData) {
    if (companyData) {
      try {
        sessionStorage.setItem(this.MBASE_COMPANYDATA_ID, this.securityService.encrypt(JSON.stringify(companyData)));
        return true;
      } catch (e) {
        console.log('Fail saving company data in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }

  public setUserData(userData) {
    if (userData) {
      try {
        sessionStorage.setItem(this.MBASE_USERDATA_ID, this.securityService.encrypt(JSON.stringify(userData)));
        return true;
      } catch (e) {
        console.log('Fail saving user data in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }

  public setCenterList(centerList) {
    if (centerList) {
      try {
        const sortedCenterList = centerList.sort((a, b) => a.centerName.toLowerCase() > b.centerName.toLowerCase() ? 1 : -1 );
        sessionStorage.setItem(this.MBASE_CENTERLIST_ID, this.securityService.encrypt(JSON.stringify(sortedCenterList)));
        return true;
      } catch (e) {
        console.log('Fail saving center list in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }

  public setSocketChannels(socketChannels) {
    if (socketChannels) {
      try {
        sessionStorage.setItem(this.MBASE_SOCKETCHANNELS_ID, this.securityService.encrypt(JSON.stringify(socketChannels)));
        return true;
      } catch (e) {
        console.log('Fail saving socket channels list in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }

  public setMenuSectionList(menuSectionList) {
    if (menuSectionList) {
      try {
        sessionStorage.setItem(this.MBASE_MENUSECTIONLIST_ID, this.securityService.encrypt(JSON.stringify(menuSectionList)));
        return true;
      } catch (e) {
        console.log('Fail saving menu section list in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }

  public setBOVersion(boVersion) {
    if (boVersion) {
      try {
        sessionStorage.setItem(this.MBASE_BO_VERSION, this.securityService.encrypt(JSON.stringify(boVersion)));
        return true;
      } catch (e) {
        console.log('Fail saving BO version in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public setOAuthToken(oAuthToken) {
    if (oAuthToken) {
      try {
        sessionStorage.setItem(this.MBASE_OAUTH_TOKEN, this.securityService.encrypt(JSON.stringify(oAuthToken)));
        return true;
      } catch (e) {
        console.log('Fail saving oauth token in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public setFilterDevices(filterDevices) {
    if (filterDevices) {
      try {
        sessionStorage.setItem(this.MBASE_FILTER_DEVICES, this.securityService.encrypt(JSON.stringify(filterDevices)));
        return true;
      } catch (e) {
        console.log('Fail saving filter devices in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public setFilterCatalogs(filterCatalogs) {
    if (filterCatalogs) {
      try {
        sessionStorage.setItem(this.MBASE_FILTER_CATALOGS, this.securityService.encrypt(JSON.stringify(filterCatalogs)));
        return true;
      } catch (e) {
        console.log('Fail saving filter catalogs in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public setFilterBroadcastings(filterBroadcastings) {
    if (filterBroadcastings) {
      try {
        sessionStorage.setItem(this.MBASE_FILTER_BROADCASTINGS, this.securityService.encrypt(JSON.stringify(filterBroadcastings)));
        return true;
      } catch (e) {
        console.log('Fail saving filter devices in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public setFilterContexts(filterContexts) {
    if (filterContexts) {
      try {
        sessionStorage.setItem(this.MBASE_FILTER_CONTEXTS, this.securityService.encrypt(JSON.stringify(filterContexts)));
        return true;
      } catch (e) {
        console.log('Fail saving filter contexts in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public setFilterCenters(filterCenters) {
    if (filterCenters) {
      try {
        sessionStorage.setItem(this.MBASE_FILTER_CENTERS, this.securityService.encrypt(JSON.stringify(filterCenters)));
        return true;
      } catch (e) {
        console.log('Fail saving filter centers in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public setFilterNotifications(filterNotifications) {
    if (filterNotifications) {
      try {
        sessionStorage.setItem(this.MBASE_FILTER_NOTIFICATIONS, this.securityService.encrypt(JSON.stringify(filterNotifications)));
        return true;
      } catch (e) {
        console.log('Fail saving filter notifications in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public setFilterTickets(filterTickets) {
    if (filterTickets) {
      try {
        sessionStorage.setItem(this.MBASE_FILTER_TICKETS, this.securityService.encrypt(JSON.stringify(filterTickets)));
        return true;
      } catch (e) {
        console.log('Fail saving filter tickets in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public setFilterLocations(filterLocations) {
    if (filterLocations) {
      try {
        sessionStorage.setItem(this.MBASE_FILTER_LOCATIONS, this.securityService.encrypt(JSON.stringify(filterLocations)));
        return true;
      } catch (e) {
        console.log('Fail saving filter locations in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public setFilterForms(filterForms) {
    if (filterForms) {
      try {
        sessionStorage.setItem(this.MBASE_FILTER_FORMS, this.securityService.encrypt(JSON.stringify(filterForms)));
        return true;
      } catch (e) {
        console.log('Fail saving filter forms in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public setFilterAlerts(alertsForms) {
    if (alertsForms) {
      try {
        sessionStorage.setItem(this.MBASE_FILTER_ALERTS, this.securityService.encrypt(JSON.stringify(alertsForms)));
        return true;
      } catch (e) {
        console.log('Fail saving alerts forms in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public setFilterStaffs(staffsForms) {
    if (staffsForms) {
      try {
        sessionStorage.setItem(this.MBASE_FILTER_STAFFS, this.securityService.encrypt(JSON.stringify(staffsForms)));
        return true;
      } catch (e) {
        console.log('Fail saving staffs forms in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public setFilterDepartments(departmentsForms) {
    if (departmentsForms) {
      try {
        sessionStorage.setItem(this.MBASE_FILTER_DEPARTMENTS, this.securityService.encrypt(JSON.stringify(departmentsForms)));
        return true;
      } catch (e) {
        console.log('Fail saving departments forms in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public setFilterDesks(desksForms) {
    if (desksForms) {
      try {
        sessionStorage.setItem(this.MBASE_FILTER_DESKS, this.securityService.encrypt(JSON.stringify(desksForms)));
        return true;
      } catch (e) {
        console.log('Fail saving desks forms in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public setFilterGateways(gatewaysForms) {
    if (gatewaysForms) {
      try {
        sessionStorage.setItem(this.MBASE_FILTER_GATEWAYS, this.securityService.encrypt(JSON.stringify(gatewaysForms)));
        return true;
      } catch (e) {
        console.log('Fail saving gateways forms in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public setFilterLicenses(licensesForms) {
    if (licensesForms) {
      try {
        sessionStorage.setItem(this.MBASE_FILTER_LICENSES, this.securityService.encrypt(JSON.stringify(licensesForms)));
        return true;
      } catch (e) {
        console.log('Fail saving licenses forms in browser. ' + e);
        return false;
      }
    } else {
      return false;
    }
  }
  public getUDID() {
    if (sessionStorage.getItem(this.MBASE_UDID_ID)) {
      try {
        const udid = this.securityService.decrypt(sessionStorage.getItem(this.MBASE_UDID_ID));
        return udid;
      } catch (e) {
        console.log('Fail retrieving udid token from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }

  public getContext() {
    if (sessionStorage.getItem(this.MBASE_CONTEXT_ID)) {
      try {
        const context = this.securityService.decrypt(sessionStorage.getItem(this.MBASE_CONTEXT_ID));
        return context;
      } catch (e) {
        console.log('Fail retrieving context token from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }

  public getLang() {
    if (sessionStorage.getItem(this.MBASE_LANG_ID)) {
      try {
        const context = this.securityService.decrypt(sessionStorage.getItem(this.MBASE_LANG_ID));
        return context;
      } catch (e) {
        console.log('Fail retrieving lang token from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }

  public getCompanList() {
    if (sessionStorage.getItem(this.MBASE_COMPANY_LIST)) {
      try {
        const companyList = new Array<CompanyData>();
        Object.assign(companyList, JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_COMPANY_LIST))));
        return companyList;
      } catch (e) {
        console.log('Fail retrieving company list data from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }

  public getTmpContext() {
    if (sessionStorage.getItem(this.MBASE_TMP_CONTEXT)) {
      try {
        const context = this.securityService.decrypt(sessionStorage.getItem(this.MBASE_TMP_CONTEXT));
        return context;
      } catch (e) {
        console.log('Fail retrieving temporal context token from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }

  public getLoginVerificationChallenge() {
    if (sessionStorage.getItem(this.MBASE_LOGIN_VERIFICATION_CHALLENGE)) {
      try {
        const rawChallenge = JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_LOGIN_VERIFICATION_CHALLENGE)));
        const challenge = new LoginVerificationChallenge();
        const contacts = new Array<LoginVerificationContact>();
        Object.assign(challenge, rawChallenge);
        if (rawChallenge['contacts']) {
          rawChallenge['contacts'].forEach(
            (contact) => {
              contacts.push(Object.assign(new LoginVerificationContact(), contact));
            }
          );
        }
        challenge.Contacts = contacts;
        return challenge;
      } catch (e) {
        console.log('Fail retrieving login verification challenge from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }

  public getCompanyData() {
    if (sessionStorage.getItem(this.MBASE_COMPANYDATA_ID)) {
      try {
        const companyData = new CompanyData();
        Object.assign(companyData, JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_COMPANYDATA_ID))));
        return companyData;
      } catch (e) {
        console.log('Fail retrieving company data from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }

  public getUserData() {
    if (sessionStorage.getItem(this.MBASE_USERDATA_ID)) {
      try {
        const userData = new UserData();
        Object.assign(userData, JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_USERDATA_ID))));
        return userData;
      } catch (e) {
        console.log('Fail retrieving user data from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }

  public getSocketChannels() {
    if (sessionStorage.getItem(this.MBASE_SOCKETCHANNELS_ID)) {
      try {
        const socketChannels = new Array<SocketChannel>();
        JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_SOCKETCHANNELS_ID))).forEach(
          (sc) => {
            socketChannels.push(Object.assign(new SocketChannel(), sc));
          }
        );
        return socketChannels;
      } catch (e) {
        console.log('Fail retrieving socket channels from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }

  public getCenterList(parameter?: string) {
    if (sessionStorage.getItem(this.MBASE_CENTERLIST_ID)) {
      try {
        if (!parameter) {
          const centers = new Array<CenterLite>();
          JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_CENTERLIST_ID))).forEach(
            (c) => {
              centers.push(Object.assign(new CenterLite(), c));
            }
          );
          return centers;
        } else {
          const arr = new Array<any>();
          JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_CENTERLIST_ID))).forEach(
            (c) => {
              const aux = new CenterLite();
              Object.assign(aux, c);
              arr.push(eval('aux.' + parameter));
            }
          );
        }
      } catch (e) {
        console.log('Fail retrieving center list from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }

  public getMenuSectionList() {
    if (sessionStorage.getItem(this.MBASE_MENUSECTIONLIST_ID)) {
      try {
        const menuSectionList = new Array<MenuSection>();
        JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_MENUSECTIONLIST_ID))).forEach(
          (s) => {
            menuSectionList.push(Object.assign(new MenuSection(), s));
          }
        );
        return menuSectionList;
      } catch (e) {
        console.log('Fail retrieving menu section list from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }

  public getBOVersion() {
    if (sessionStorage.getItem(this.MBASE_BO_VERSION)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_BO_VERSION)));
      } catch (e) {
        console.log('Fail retrieving BO version from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public getOAuthToken() {
    if (sessionStorage.getItem(this.MBASE_OAUTH_TOKEN)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_OAUTH_TOKEN)));
      } catch (e) {
        console.log('Fail retrieving oauth token from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public getFilterDevices() {
    if (sessionStorage.getItem(this.MBASE_FILTER_DEVICES)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_FILTER_DEVICES)));
      } catch (e) {
        console.log('Fail retrieving filter devices from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public getFilterCatalogs() {
    if (sessionStorage.getItem(this.MBASE_FILTER_CATALOGS)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_FILTER_CATALOGS)));
      } catch (e) {
        console.log('Fail retrieving filter catalogs from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public getFilterBroadcastings() {
    if (sessionStorage.getItem(this.MBASE_FILTER_BROADCASTINGS)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_FILTER_BROADCASTINGS)));
      } catch (e) {
        console.log('Fail retrieving filter broadcastings from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public getFilterContexts() {
    if (sessionStorage.getItem(this.MBASE_FILTER_CONTEXTS)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_FILTER_CONTEXTS)));
      } catch (e) {
        console.log('Fail retrieving filter contexts from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public getFilterCenters() {
    if (sessionStorage.getItem(this.MBASE_FILTER_CENTERS)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_FILTER_CENTERS)));
      } catch (e) {
        console.log('Fail retrieving filter centers from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public getFilterNotifications() {
    if (sessionStorage.getItem(this.MBASE_FILTER_NOTIFICATIONS)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_FILTER_NOTIFICATIONS)));
      } catch (e) {
        console.log('Fail retrieving filter notifications from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public getFilterTickets() {
    if (sessionStorage.getItem(this.MBASE_FILTER_TICKETS)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_FILTER_TICKETS)));
      } catch (e) {
        console.log('Fail retrieving filter tickets from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public getFilterLocations() {
    if (sessionStorage.getItem(this.MBASE_FILTER_LOCATIONS)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_FILTER_LOCATIONS)));
      } catch (e) {
        console.log('Fail retrieving filter locations from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public getFilterForms() {
    if (sessionStorage.getItem(this.MBASE_FILTER_FORMS)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_FILTER_FORMS)));
      } catch (e) {
        console.log('Fail retrieving filter forms from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public getFilterAlerts() {
    if (sessionStorage.getItem(this.MBASE_FILTER_ALERTS)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_FILTER_ALERTS)));
      } catch (e) {
        console.log('Fail retrieving filter alerts from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public getFilterStaffs() {
    if (sessionStorage.getItem(this.MBASE_FILTER_STAFFS)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_FILTER_STAFFS)));
      } catch (e) {
        console.log('Fail retrieving filter staffs from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public getFilterDepartments() {
    if (sessionStorage.getItem(this.MBASE_FILTER_DEPARTMENTS)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_FILTER_DEPARTMENTS)));
      } catch (e) {
        console.log('Fail retrieving filter departments from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public getFilterDesks() {
    if (sessionStorage.getItem(this.MBASE_FILTER_DESKS)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_FILTER_DESKS)));
      } catch (e) {
        console.log('Fail retrieving filter desks from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public getFilterGateways() {
    if (sessionStorage.getItem(this.MBASE_FILTER_GATEWAYS)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_FILTER_GATEWAYS)));
      } catch (e) {
        console.log('Fail retrieving filter gateways from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public getFilterLicenses() {
    if (sessionStorage.getItem(this.MBASE_FILTER_LICENSES)) {
      try {
        return JSON.parse(this.securityService.decrypt(sessionStorage.getItem(this.MBASE_FILTER_LICENSES)));
      } catch (e) {
        console.log('Fail retrieving filter licenses from browser. ' + e);
        return null;
      }
    } else {
      return null;
    }
  }
  public deleteFilterDevices() {
    sessionStorage.removeItem(this.MBASE_FILTER_DEVICES);
  }
  public deleteFilterCatalogs() {
    sessionStorage.removeItem(this.MBASE_FILTER_CATALOGS);
  }
  public deleteFilterBroadcastings() {
    sessionStorage.removeItem(this.MBASE_FILTER_BROADCASTINGS);
  }
  public deleteFilterContexts() {
    sessionStorage.removeItem(this.MBASE_FILTER_CONTEXTS);
  }
  public deleteFilterCenters() {
    sessionStorage.removeItem(this.MBASE_FILTER_CENTERS);
  }
  public deleteFilterNotifications() {
    sessionStorage.removeItem(this.MBASE_FILTER_NOTIFICATIONS);
  }
  public deleteFilterTickets() {
    sessionStorage.removeItem(this.MBASE_FILTER_TICKETS);
  }
  public deleteFilterLocations() {
    sessionStorage.removeItem(this.MBASE_FILTER_LOCATIONS);
  }
  public deleteFilterForms() {
    sessionStorage.removeItem(this.MBASE_FILTER_FORMS);
  }
  public deleteFilterAlerts() {
    sessionStorage.removeItem(this.MBASE_FILTER_ALERTS);
  }
  public deleteFilterStaffs() {
    sessionStorage.removeItem(this.MBASE_FILTER_STAFFS);
  }
  public deleteFilterDepartments() {
    sessionStorage.removeItem(this.MBASE_FILTER_DEPARTMENTS);
  }
  public deleteFilterDesks() {
    sessionStorage.removeItem(this.MBASE_FILTER_DESKS);
  }
  public deleteFilterGateways() {
    sessionStorage.removeItem(this.MBASE_FILTER_GATEWAYS);
  }
  public deleteFilterLicenses() {
    sessionStorage.removeItem(this.MBASE_FILTER_LICENSES);
  }
  public deleteLoginVerificationChallenge() {
    sessionStorage.removeItem(this.MBASE_LOGIN_VERIFICATION_CHALLENGE);
  }
  public deleteAllItems() {
    sessionStorage.removeItem(this.MBASE_UDID_ID);
    sessionStorage.removeItem(this.MBASE_LANG_ID);
    sessionStorage.removeItem(this.MBASE_CONTEXT_ID);
    sessionStorage.removeItem(this.MBASE_COMPANYDATA_ID);
    sessionStorage.removeItem(this.MBASE_USERDATA_ID);
    sessionStorage.removeItem(this.MBASE_CENTERLIST_ID);
    sessionStorage.removeItem(this.MBASE_MENUSECTIONLIST_ID);
    sessionStorage.removeItem(this.MBASE_BO_VERSION);
    sessionStorage.removeItem(this.MBASE_FILTER_DEVICES);
    sessionStorage.removeItem(this.MBASE_FILTER_BROADCASTINGS);
    sessionStorage.removeItem(this.MBASE_FILTER_CONTEXTS);
    sessionStorage.removeItem(this.MBASE_FILTER_CENTERS);
    sessionStorage.removeItem(this.MBASE_FILTER_NOTIFICATIONS);
    sessionStorage.removeItem(this.MBASE_FILTER_TICKETS);
    sessionStorage.removeItem(this.MBASE_FILTER_LOCATIONS);
    sessionStorage.removeItem(this.MBASE_FILTER_FORMS);
    sessionStorage.removeItem(this.MBASE_FILTER_ALERTS);
    sessionStorage.removeItem(this.MBASE_FILTER_STAFFS);
    sessionStorage.removeItem(this.MBASE_FILTER_DEPARTMENTS);
    sessionStorage.removeItem(this.MBASE_FILTER_DESKS);
    sessionStorage.removeItem(this.MBASE_FILTER_GATEWAYS);
    sessionStorage.removeItem(this.MBASE_FILTER_LICENSES);
    sessionStorage.removeItem(this.MBASE_LOGIN_VERIFICATION_CHALLENGE);
  }
  public checkSession() {
    if (this.getUDID() && this.getContext() && this.getCompanyData() && this.getUserData() && this.getCenterList() && this.getMenuSectionList()) {
      return true;
    } else {
      return false;
    }
  }

}
